import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import { getDroppedOrSelectedFiles } from 'html5-file-selector';

import Button from 'components/Button';
import { FilesTab } from 'apps/repository/views/files';
import { ActivityLogTab } from 'apps/repository/views/activityLog';
import { Section } from 'legacyComponents/layout/section';

import { Center } from 'legacyComponents/styled/lib';
import { ButtonsWrapper, InputFileStyled } from 'apps/repository/styles';
import { SectionBanner, Paragraph, Box } from 'components';
import { BANNER_ACTIONS } from 'constants/analytics';
import { analytics } from 'core/analytics';

@withNamespaces()
@observer
export class Repository extends React.Component {
  addFiles = e => {
    const { repoManager } = this.props;
    getDroppedOrSelectedFiles(e).then(list => {
      if (list.length) repoManager.AddFiles(list);
    });
    this.fileInput.value = '';
  };

  componentDidCatch = (error, info) => {
    const { repoManager } = this.props;
    repoManager.CatchError(error, info);
  };

  render() {
    const {
      title = '',
      projectManager,
      returnLink,
      repoManager,
      crumbs = null,
      t,
    } = this.props;
    const { project } = projectManager;

    let subTitle = repoManager.variables.version
      ? `(#${repoManager.variables.version})`
      : '';

    const { hasError, repository, atHead } = repoManager;

    if (project === null || repository === null) {
      return (
        <React.Fragment>
          <Section title={title} subTitle={t(subTitle)}>
            {crumbs}
            <FilesTab
              condition={!hasError}
              app={repository}
              project={project}
              manager={repoManager}
              returnLink={returnLink}
            />
          </Section>
        </React.Fragment>
      );
    }

    const { contributionCount } = repoManager;
    const { SetFolderInputVisibility } = repoManager;
    const {
      inSpace: { whichTypes },
    } = project;

    const canContribute = whichTypes.includes('Contribution') && atHead;

    if (atHead) {
      subTitle =
        repository?.pendingOperations.length > 0
          ? `(${t('{{operationsCount}} local change', {
              operationsCount: repository.pendingOperations.length,
            })})`
          : `(${t('latest')})`;
    }

    const runOnChange = event => {
      this.addFiles(event);
    };

    const Buttons = canContribute && (
      <ButtonsWrapper nextToCrumbs={crumbs}>
        <Button
          text={<Trans>New folder</Trans>}
          size='small'
          appearance='plain'
          onClick={() => SetFolderInputVisibility(true)}
          icon='folder'
        />
        <Button
          text={<Trans>Upload file</Trans>}
          appearance='plain'
          size='small'
          htmlFor='upload_file'
          data-test-id='upload-file-icon-button'
          as='label'
          icon='upload'
        />

        <Button
          text={<Trans>Upload folder</Trans>}
          appearance='plain'
          size='small'
          htmlFor='upload_folder'
          as='label'
          icon='folder'
        />

        <input
          type='file'
          multiple
          onChange={runOnChange}
          directory='true'
          webkitdirectory='true'
          mozdirectory='true'
          allowdirs=''
          id='upload_folder'
          style={{ display: 'none' }}
        ></input>

        <InputFileStyled
          type='file'
          ref={ref => {
            this.fileInput = ref;
          }}
          id='upload_file'
          multiple={true}
          onChange={this.addFiles}
        />
      </ButtonsWrapper>
    );

    return (
      <>
        <Section title={t(title)} subTitle={t(subTitle)}>
          {crumbs}
          {Buttons}
          <FilesTab
            condition={!hasError}
            app={repository}
            project={project}
            manager={repoManager}
            returnLink={returnLink}
          />
          {hasError && (
            <Center>
              <Trans>
                Ooops, an error related to the repository has been detected.
              </Trans>
            </Center>
          )}
        </Section>
        <SectionBanner
          bannerId='project-files-history-log'
          imageSrc='/assets/banners/001.svg'
          title='Boost your chances of a great quote.'
          buttonText='Get a Quote'
          linkName='https://wikifactory.com/make-part'
          paddingTop='xlarge'
          paddingBottom='xxlarge'
          eventCallback={() =>
            analytics.sendEvent(BANNER_ACTIONS.PROJECT_FILES)
          }
        >
          <Box>
            <Paragraph>
              <strong>Whether for prototyping or end-use</strong>, your parts
              can be produced with a quality assured manufactured from our
              global network. <strong>Get a quote in 24 hours!</strong>
            </Paragraph>
            <Paragraph>
              With Design for Manufacturing (DFM) feedback included, we
              guarantee
              <strong>
                {' '}
                optimised quotes to the precision, price, and speed
              </strong>{' '}
              you need.
            </Paragraph>
          </Box>
        </SectionBanner>
        {returnLink ? (
          <React.Fragment />
        ) : (
          <>
            <Section
              title={t('History')}
              subTitle={t('({{ contributionCount }})', { contributionCount })}
              last
            >
              <ActivityLogTab
                condition={!hasError}
                app={repository}
                project={project}
                manager={repoManager}
                returnLink={returnLink}
              />
            </Section>
          </>
        )}
      </>
    );
  }
}

Repository.propTypes = {
  title: PropTypes.string,
  projectManager: PropTypes.object,
  repoManager: PropTypes.object,
  returnLink: PropTypes.object,
  crumbs: PropTypes.node,
  t: PropTypes.func,
};
